import { useEffect, useState } from "react";
import designSystems from "./data/designSystems.json";
import { HealthBadge } from "./HealthBadge";

type Locale = "zh" | "en";

interface DesignSystem {
  title: string;
  description: Record<Locale, string>;
  website: string;
  github: string;
  image: string | null;
  tags: Array<Record<Locale, string>>;
}

interface TagOption {
  key: string;
  label: Record<Locale, string>;
}

const preloadedImageUrls = new Set<string>();
const PRELOAD_IMAGE_COUNT = 12;
const REPOSITORY_URL = "https://github.com/wuxia101/awesome-ui-design-system.git";

function preloadImage(url: string) {
  if (preloadedImageUrls.has(url)) {
    return;
  }

  preloadedImageUrls.add(url);

  const image = new Image();
  image.decoding = "async";
  image.src = url;
}

const localeCopy: Record<
  Locale,
  {
    pageTitle: string;
    pageDescription: string;
    repositoryLink: string;
    officialSite: string;
    github: string;
    controlsToggle: string;
    controlsHide: string;
    activeFilters: (count: number) => string;
    searchPlaceholder: string;
    searchLabel: string;
    allTags: string;
    filterLabel: string;
    resultCount: (count: number) => string;
    emptyState: string;
  }
> = {
  zh: {
    pageTitle: "设计系统收藏",
    pageDescription: "收藏精选优质设计系统，覆盖设计规范、组件库与产品实践。",
    repositoryLink: "GitHub 仓库",
    officialSite: "官网",
    github: "GitHub",
    controlsToggle: "展开搜索和标签",
    controlsHide: "收起搜索和标签",
    activeFilters: (count) => `已启用 ${count} 个筛选`,
    searchPlaceholder: "搜索名称、描述或标签",
    searchLabel: "搜索",
    allTags: "全部",
    filterLabel: "按标签筛选",
    resultCount: (count) => `共 ${count} 个结果`,
    emptyState: "当前筛选条件下暂无设计系统。",
  },
  en: {
    pageTitle: "Design System Collection",
    pageDescription: "A curated set of quality design systems spanning guidelines, component libraries, and product practices.",
    repositoryLink: "GitHub Repository",
    officialSite: "Website",
    github: "GitHub",
    controlsToggle: "Show search and tags",
    controlsHide: "Hide search and tags",
    activeFilters: (count) => `${count} active filter${count === 1 ? "" : "s"}`,
    searchPlaceholder: "Search by title, description, or tag",
    searchLabel: "Search",
    allTags: "All",
    filterLabel: "Filter by tag",
    resultCount: (count) => `${count} result${count === 1 ? "" : "s"}`,
    emptyState: "No design systems match the current filters.",
  },
};

export function DesignSystems() {
  const [locale, setLocale] = useState<Locale>("zh");
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isMobileControlsOpen, setIsMobileControlsOpen] = useState(false);
  const copy = localeCopy[locale];
  const systems = designSystems as DesignSystem[];
  const tagMap = new Map<string, TagOption>();

  for (const system of systems) {
    for (const tag of system.tags) {
      if (!tagMap.has(tag.en)) {
        tagMap.set(tag.en, {
          key: tag.en,
          label: tag,
        });
      }
    }
  }

  const tags = Array.from(tagMap.values()).sort((a, b) =>
    a.label[locale].localeCompare(b.label[locale], locale === "zh" ? "zh-CN" : "en"),
  );
  const normalizedQuery = searchQuery.trim().toLocaleLowerCase();
  const activeFilterCount = (selectedTag ? 1 : 0) + (normalizedQuery ? 1 : 0);
  const filteredSystems = systems.filter((system) => {
    const matchesTag = selectedTag
      ? system.tags.some((tag) => tag.en === selectedTag)
      : true;

    if (!matchesTag) {
      return false;
    }

    if (!normalizedQuery) {
      return true;
    }

    const searchHaystack = [
      system.title,
      system.description[locale],
      ...system.tags.map((tag) => tag[locale]),
      ...system.tags.map((tag) => tag.en),
    ]
      .join(" ")
      .toLocaleLowerCase();

    return searchHaystack.includes(normalizedQuery);
  });

  useEffect(() => {
    document.documentElement.lang = locale === "zh" ? "zh-CN" : "en";
  }, [locale]);

  useEffect(() => {
    filteredSystems
      .slice(0, PRELOAD_IMAGE_COUNT)
      .map((system) => system.image)
      .filter((image): image is string => Boolean(image))
      .forEach(preloadImage);
  }, [filteredSystems]);

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm sticky top-0 z-10 px-4 py-3">
        <div className="max-w-7xl mx-auto flex flex-col gap-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div className="min-w-0">
              <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">{copy.pageTitle}</h1>
              <p className="mt-1 text-sm leading-6 text-gray-500">{copy.pageDescription}</p>
            </div>
            <div className="flex flex-wrap items-center gap-3 self-start">
              <a
                href={REPOSITORY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:border-gray-300 hover:bg-gray-50 hover:text-gray-900"
              >
                <svg
                  aria-hidden="true"
                  viewBox="0 0 24 24"
                  className="h-4 w-4 fill-current"
                >
                  <path d="M12 .5C5.65.5.5 5.8.5 12.33c0 5.23 3.3 9.67 7.88 11.23.58.11.79-.26.79-.57 0-.28-.01-1.21-.02-2.19-3.2.71-3.88-1.39-3.88-1.39-.52-1.37-1.28-1.73-1.28-1.73-1.05-.73.08-.71.08-.71 1.16.08 1.77 1.23 1.77 1.23 1.03 1.82 2.7 1.29 3.36.99.1-.77.4-1.29.72-1.58-2.55-.3-5.24-1.31-5.24-5.86 0-1.29.45-2.34 1.19-3.17-.12-.3-.52-1.51.11-3.15 0 0 .97-.32 3.19 1.21a10.8 10.8 0 0 1 5.8 0c2.21-1.53 3.18-1.21 3.18-1.21.64 1.64.24 2.85.12 3.15.74.83 1.18 1.88 1.18 3.17 0 4.56-2.7 5.56-5.26 5.85.41.36.78 1.08.78 2.18 0 1.58-.01 2.85-.01 3.23 0 .31.21.69.8.57 4.57-1.57 7.87-6 7.87-11.23C23.5 5.8 18.35.5 12 .5Z" />
                </svg>
                {copy.repositoryLink}
              </a>
              <div
                className="inline-flex w-fit rounded-full border border-gray-200 bg-gray-100 p-1"
                role="group"
                aria-label="language switcher"
              >
                {(["zh", "en"] as const).map((nextLocale) => {
                  const active = locale === nextLocale;
                  return (
                    <button
                      key={nextLocale}
                      type="button"
                      onClick={() => setLocale(nextLocale)}
                      className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                        active
                          ? "bg-white text-gray-900 shadow-sm"
                          : "text-gray-500 hover:text-gray-900"
                      }`}
                    >
                      {nextLocale === "zh" ? "中文" : "English"}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-3 md:hidden">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-700">{copy.resultCount(filteredSystems.length)}</p>
                {activeFilterCount > 0 && (
                  <p className="mt-1 text-xs text-gray-500">{copy.activeFilters(activeFilterCount)}</p>
                )}
              </div>
              <button
                type="button"
                onClick={() => setIsMobileControlsOpen((current) => !current)}
                className="shrink-0 rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:border-gray-300 hover:text-gray-900"
                aria-expanded={isMobileControlsOpen}
                aria-controls="mobile-search-filters"
              >
                {isMobileControlsOpen ? copy.controlsHide : copy.controlsToggle}
              </button>
            </div>

            <div
              id="mobile-search-filters"
              className={`${isMobileControlsOpen ? "flex" : "hidden"} flex-col gap-3 md:flex`}
            >
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <label className="flex min-w-0 flex-col gap-2 lg:max-w-md lg:flex-1">
                <span className="text-sm font-medium text-gray-700">{copy.searchLabel}</span>
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder={copy.searchPlaceholder}
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                />
              </label>
              <div className="hidden md:flex md:flex-col md:gap-2 lg:items-end">
                <p className="text-sm font-medium text-gray-700">{copy.filterLabel}</p>
                <p className="text-sm text-gray-500">{copy.resultCount(filteredSystems.length)}</p>
              </div>
            </div>
            <div className="-mx-4 overflow-x-auto px-4 pb-1">
              <div className="flex w-max min-w-full gap-2 sm:w-auto sm:min-w-0 sm:flex-wrap">
                <button
                  type="button"
                  onClick={() => setSelectedTag(null)}
                  className={`rounded-full border px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
                    selectedTag === null
                      ? "border-gray-900 bg-gray-900 text-white"
                      : "border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:text-gray-900"
                  }`}
                >
                  {copy.allTags}
                </button>
                {tags.map((tag) => {
                  const active = selectedTag === tag.key;
                  return (
                    <button
                      key={tag.key}
                      type="button"
                      onClick={() => setSelectedTag(tag.key)}
                      className={`rounded-full border px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
                        active
                          ? "border-blue-600 bg-blue-600 text-white"
                          : "border-gray-200 bg-white text-gray-600 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
                      }`}
                    >
                      {tag.label[locale]}
                    </button>
                  );
                })}
              </div>
            </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 px-4 py-6 sm:py-8">
        <div className="max-w-7xl mx-auto">
          {filteredSystems.length > 0 ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3 xl:grid-cols-4">
              {filteredSystems.map((ds, index) => (
                <article
                  key={ds.title}
                  className="group overflow-hidden rounded-xl bg-white shadow-sm transition-all duration-200 hover:shadow-md hover:outline hover:outline-2 hover:outline-blue-400"
                >
                  <div className="aspect-video overflow-hidden bg-gray-100 flex items-center justify-center">
                    {ds.image && (
                      <img
                        src={ds.image}
                        alt={ds.title}
                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                        loading={index < PRELOAD_IMAGE_COUNT ? "eager" : "lazy"}
                        fetchPriority={index < 4 ? "high" : "auto"}
                      />
                    )}
                    {!ds.image && (
                      <div className="text-4xl font-bold text-gray-400">
                        {ds.title.charAt(0)}
                      </div>
                    )}
                  </div>
                  <div className="p-4 sm:p-5">
                    <h3 className="text-base font-semibold text-gray-900 transition-colors group-hover:text-blue-600 sm:text-lg">
                      {ds.title}
                    </h3>
                    <p className="mt-2 text-sm leading-6 text-gray-500 line-clamp-3">
                      {ds.description[locale]}
                    </p>
                    <div className="mt-4 border-t border-gray-100 pt-4">
                      <div className="flex flex-wrap gap-2">
                        {ds.tags.map((tag) => (
                          <span
                            key={`${ds.title}-${tag.en}`}
                            className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600 transition-colors group-hover:bg-blue-50 group-hover:text-blue-700"
                          >
                            {`【${tag[locale]}】`}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="mt-3 flex items-center justify-between border-t border-gray-100 pt-3">
                      <HealthBadge url={ds.website} locale={locale} />
                    </div>
                    <div className="mt-3 flex flex-col gap-3 sm:flex-row">
                      <a
                        href={ds.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-700"
                      >
                        {copy.officialSite}
                      </a>
                      <a
                        href={ds.github}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:border-gray-300 hover:bg-gray-50"
                      >
                        {copy.github}
                      </a>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-gray-300 bg-white/70 px-6 py-12 text-center text-sm text-gray-500">
              {copy.emptyState}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
